import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme, css } from 'styled-components';
import { cardBackground, cardBorder, cardShadow } from '../styles/themeHelpers';
import { useRefereeAuth } from '../contexts/RefereeContext';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/common/NotificationContainer';
import GamesSection from '../components/field/GamesSection';
import { RefereeScheduleSection } from '../components/schedule';
import refereeService, { RefereeProfileDto, RefereeContactInfoDto } from '../services/refereeService';
import { ContactInfoDto, hasAnyContactInfo } from '../types/contactInfo';
import { isValidEmail, isValidUrl, ValidationMessages } from '../utils/validation';
import PhoneInput from '../components/common/PhoneInput';
import { useAuth } from '../contexts/AuthContext';
import { useFieldAuth } from '../contexts/FieldContext';
import RefereeAchievements from '../components/referee/RefereeAchievements';
import ModernBackground from '../components/common/ModernBackground';
import ReviewSummary from '../components/common/ReviewSummary';
import ReviewModal from '../components/common/ReviewModal';
import reviewService from '../services/reviewService';
import { ReviewSummary as ReviewSummaryType, Review } from '../types/review';

// Theme helpers
const shadowCardBackground = (theme: any) => cardBackground(theme);
const shadowCardBorder = (theme: any) => cardBorder(theme);
const shadowCardShadow = (theme: any) => cardShadow(theme);
const shadowHeaderBackground = (theme: any) => theme.styles?.headerBackground ?? shadowCardBackground(theme);

const shadowGridChrome = css`
  ${({ theme }) => theme.name === 'shadowGrid' && css`
    position: relative;
    border: 1px solid rgba(0, 212, 255, 0.35);
    background: ${cardBackground(theme)};
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.6), 0 0 28px rgba(0, 212, 255, 0.2);
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid rgba(0, 212, 255, 0.2);
      opacity: 0.25;
      pointer-events: none;
    }
  `}
`;

interface ThemeProps {
  $night?: boolean;
}

const Background = styled.div<ThemeProps>`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients.background || theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div<ThemeProps>`
  max-width: 1200px;
  margin: 0 auto;
  padding: 100px 20px 20px 20px;
  position: relative;
  z-index: 2;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const Header = styled.div<ThemeProps>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  padding: 30px;
  background: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return theme.colors.surface;
    return $night ? theme.colors.surface : shadowHeaderBackground(theme);
  }};
  border: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return `1px solid ${theme.colors.borderLight}`;
    return $night ? 'none' : `1px solid ${shadowCardBorder(theme)}`;
  }};
  border-radius: ${({ $night }) => ($night ? '0' : '18px')};
  ${({ $night, theme }) => (!$night && theme.name === 'shadowGrid') && shadowGridChrome};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Title = styled.h1<ThemeProps>`
  color: ${({ $night, theme }) => ($night ? theme.colors.primary : theme.colors.primary)};
  margin: 0;
  font-size: 2.5rem;
  font-weight: bold;
  text-shadow: 0 0 20px ${({ theme }) => `${theme.colors.primary}50`};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  text-transform: ${({ $night }) => ($night ? 'uppercase' : 'none')};
  letter-spacing: ${({ $night }) => ($night ? '2px' : 'normal')};

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.div<ThemeProps>`
  color: ${({ $night, theme }) => ($night ? theme.colors.textSecondary : theme.colors.textSecondary)};
  font-size: 0.9rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
  margin-bottom: 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Section = styled.div<ThemeProps>`
  background: ${({ $night, theme }) => {
    if ($night || theme.name === 'modernWarfare') return theme.colors.surface;
    return shadowCardBackground(theme);
  }};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : shadowCardBorder(theme))};
  border-radius: ${({ $night }) => ($night ? '16px' : '16px')};
  padding: 30px;
  transition: all 0.3s ease;
  box-shadow: ${({ $night, theme }) => ($night ? theme.shadows.card : shadowCardShadow(theme))};
  ${({ theme }) => theme.name === 'shadowGrid' && shadowGridChrome};

  &:hover {
    border-color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.border : shadowCardBorder(theme))};
  }
`;

const SectionTitle = styled.h2<ThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : theme.colors.primary)};
  font-size: 1.5rem;
  margin: 0 0 20px 0;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  text-transform: ${({ $night }) => ($night ? 'uppercase' : 'none')};
  letter-spacing: ${({ $night }) => ($night ? '1px' : 'normal')};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
`;

const StatCard = styled.div<ThemeProps>`
  background: ${({ $night, theme }) => {
    if ($night || theme.name === 'modernWarfare') return theme.colors.surfaceHover;
    return 'rgba(0, 0, 0, 0.2)';
  }};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderMuted : 'rgba(0, 212, 255, 0.15)')};
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const StatValue = styled.div<ThemeProps>`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 2rem;
  font-weight: bold;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  margin-bottom: 5px;
`;

const StatLabel = styled.div<ThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.textSecondary : theme.colors.textSecondary)};
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const RankSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const RankIcon = styled.div<ThemeProps>`
  width: 80px;
  height: 80px;
  background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.surfaceHover : 'rgba(0, 0, 0, 0.3)')};
  border: 2px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RankIconImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
`;

const RankIconPlaceholder = styled.div<ThemeProps>`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  text-align: center;
`;

const RankInfo = styled.div`
  flex: 1;
`;

const RankName = styled.h3<ThemeProps>`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  margin: 0 0 5px 0;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const RankLevel = styled.div<ThemeProps>`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1rem;
  margin-bottom: 10px;
`;

const ProgressContainer = styled.div`
  width: 100%;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  overflow: hidden;
  height: 20px;
  margin-bottom: 10px;
`;

const ProgressBar = styled.div<{ width: number }>`
  height: 100%;
  background: ${({ theme }) => theme.gradients.primary || `linear-gradient(90deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`};
  width: ${props => props.width}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div<ThemeProps>`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const LoadingSpinner = styled.div<ThemeProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;

  &::after {
    content: '';
    width: 50px;
    height: 50px;
    border: 5px solid ${({ theme }) => theme.colors.borderLight};
    border-top: 5px solid ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div<ThemeProps>`
  text-align: center;
  color: #f44336;
  font-size: 1.2rem;
  padding: 60px 20px;
`;

const InfoLabel = styled.div<ThemeProps>`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  margin-bottom: 5px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const InfoValue = styled.div<ThemeProps>`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FieldCard = styled.div<ThemeProps>`
  margin-bottom: 10px;
  padding: 15px;
  background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.surfaceHover : 'rgba(0, 0, 0, 0.2)')};
  border: 1px solid ${({ theme }) => theme.colors.borderMuted};
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FieldName = styled.div<ThemeProps>`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s ease;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const EmptyState = styled.div<ThemeProps>`
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  padding: 20px;
  font-style: italic;
`;

const EditButton = styled.button<ThemeProps>`
  background: ${({ theme }) => theme.colors.primary};
  color: #000;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;

const SocialLink = styled.a<ThemeProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  padding: 8px 0;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: underline;
  }
`;

const SocialIcon = styled.span`
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div<ThemeProps>`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  padding: 30px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 850px) {
    max-width: 95%;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2<ThemeProps>`
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  font-size: 1.5rem;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const CloseButton = styled.button<ThemeProps>`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 0;
`;

const FormError = styled.span`
  color: #f44336;
  font-size: 0.75rem;
  margin-top: 4px;
  display: block;
`;

const FormLabel = styled.label<ThemeProps>`
  display: block;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: 8px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const FormInput = styled.input<ThemeProps>`
  width: 100%;
  padding: 12px 15px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.borderMuted};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 25px;
`;

const SaveButton = styled.button<ThemeProps & { disabled?: boolean }>`
  flex: 1;
  background: ${({ theme, disabled }) => disabled ? theme.colors.borderMuted : theme.colors.primary};
  color: #000;
  border: none;
  padding: 12px;
  border-radius: 8px;
  font-weight: bold;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const CancelButton = styled.button<ThemeProps>`
  flex: 1;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderMuted};
  padding: 12px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};

  &:hover {
    border-color: ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text};
  }
`;


const RefereeProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { refereeId } = useParams<{ refereeId?: string }>();
  const { referee, isRefereeAuthenticated, initializing } = useRefereeAuth();
  const { notifications, removeNotification, showError, showSuccess } = useNotification();
  const { user } = useAuth();
  const { field } = useFieldAuth();
  const theme = useTheme();
  const isNightOps = theme.name === 'nightOps';
  const isModernWarfare = theme.name === 'modernWarfare';

  // Определяем режим: публичная страница (есть refereeId) или приватная
  const isPublicMode = Boolean(refereeId);
  const targetRefereeId = refereeId || referee?.id;
  const isOwnProfile = !isPublicMode || (isRefereeAuthenticated && referee?.id === refereeId);
  const [profile, setProfile] = useState<RefereeProfileDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<{
    firstName: string;
    lastName: string;
    phone: string;
    contactInfo: ContactInfoDto;
  }>({ firstName: '', lastName: '', phone: '', contactInfo: {} });
  const [savingProfile, setSavingProfile] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    workEmail?: string;
    facebook?: string;
    instagram?: string;
    twitter?: string;
    telegram?: string;
    website?: string;
  }>({});
  const [phoneValid, setPhoneValid] = useState(true);
  const [publicPhoneValid, setPublicPhoneValid] = useState(true);

  // Review states
  const [reviewSummary, setReviewSummary] = useState<ReviewSummaryType | null>(null);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  useEffect(() => {
    // В приватном режиме ждем завершения инициализации и проверяем аутентификацию
    if (!isPublicMode) {
      if (initializing) {
        return;
      }
      if (!isRefereeAuthenticated) {
        navigate('/login');
        return;
      }
    }

    const fetchProfile = async () => {
      if (!targetRefereeId) {
        setError('Referee ID not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Используем единый метод getRefereeProfile с опциональным ID
        const profileData = await refereeService.getRefereeProfile(isPublicMode ? targetRefereeId : undefined);

        setProfile(profileData);
      } catch (error: any) {
        console.error('Error fetching referee profile:', error);
        setError(error.message || 'Failed to load profile');
        if (!isPublicMode) {
          showError('Failed to load profile', error.message || 'Failed to load profile');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [isRefereeAuthenticated, initializing, navigate, showError, isPublicMode, targetRefereeId]);

  // Load reviews
  useEffect(() => {
    const fetchReviews = async () => {
      if (!targetRefereeId) return;

      try {
        setReviewsLoading(true);
        const [summaryData, myReviewData] = await Promise.all([
          reviewService.getRefereeReviewSummary(targetRefereeId),
          user ? reviewService.getMyReview('referee', targetRefereeId) : Promise.resolve(null)
        ]);
        setReviewSummary(summaryData);
        setMyReview(myReviewData);
      } catch (err) {
        console.error('Failed to load reviews:', err);
      } finally {
        setReviewsLoading(false);
      }
    };

    fetchReviews();
  }, [targetRefereeId, user]);

  // Reload reviews after submitting
  const reloadReviews = async () => {
    if (!targetRefereeId) return;
    try {
      const [summaryData, myReviewData] = await Promise.all([
        reviewService.getRefereeReviewSummary(targetRefereeId),
        user ? reviewService.getMyReview('referee', targetRefereeId) : Promise.resolve(null)
      ]);
      setReviewSummary(summaryData);
      setMyReview(myReviewData);
    } catch (err) {
      console.error('Failed to reload reviews:', err);
    }
  };

  const handleEditProfile = () => {
    if (profile) {
      setEditingProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        contactInfo: profile.contactInfo || {}
      });
      setValidationErrors({});
      setPhoneValid(true);
      setPublicPhoneValid(true);
      setIsEditModalOpen(true);
    }
  };

  // Валидация поля при изменении
  const validateField = (field: string, value: string) => {
    let error: string | undefined;

    switch (field) {
      case 'workEmail':
        if (value && !isValidEmail(value)) {
          error = ValidationMessages.invalidEmail;
        }
        break;
      case 'facebook':
      case 'instagram':
      case 'twitter':
      case 'telegram':
      case 'website':
        if (value && !isValidUrl(value)) {
          error = ValidationMessages.invalidUrl;
        }
        break;
    }

    setValidationErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  // Проверка всех полей перед сохранением
  const validateAllFields = (): boolean => {
    const errors: typeof validationErrors = {};

    if (editingProfile.contactInfo.workEmail && !isValidEmail(editingProfile.contactInfo.workEmail)) {
      errors.workEmail = ValidationMessages.invalidEmail;
    }
    if (editingProfile.contactInfo.facebook && !isValidUrl(editingProfile.contactInfo.facebook)) {
      errors.facebook = ValidationMessages.invalidUrl;
    }
    if (editingProfile.contactInfo.instagram && !isValidUrl(editingProfile.contactInfo.instagram)) {
      errors.instagram = ValidationMessages.invalidUrl;
    }
    if (editingProfile.contactInfo.twitter && !isValidUrl(editingProfile.contactInfo.twitter)) {
      errors.twitter = ValidationMessages.invalidUrl;
    }
    if (editingProfile.contactInfo.telegram && !isValidUrl(editingProfile.contactInfo.telegram)) {
      errors.telegram = ValidationMessages.invalidUrl;
    }
    if (editingProfile.contactInfo.website && !isValidUrl(editingProfile.contactInfo.website)) {
      errors.website = ValidationMessages.invalidUrl;
    }

    setValidationErrors(errors);

    // Проверяем также валидность телефонов
    return Object.keys(errors).length === 0 && phoneValid && publicPhoneValid;
  };

  const handleSaveProfile = async () => {
    if (!isRefereeAuthenticated) return;

    // Валидация перед сохранением
    if (!validateAllFields()) {
      showError('Validation Error', 'Please fix the errors before saving');
      return;
    }

    try {
      setSavingProfile(true);
      await refereeService.updateRefereeProfile({
        firstName: editingProfile.firstName,
        lastName: editingProfile.lastName,
        phone: editingProfile.phone,
        contactInfo: editingProfile.contactInfo
      });
      showSuccess('Success', 'Profile updated successfully');
      setIsEditModalOpen(false);

      // Refresh profile
      const updatedProfile = await refereeService.getRefereeProfile();
      setProfile(updatedProfile);
    } catch (err: any) {
      showError('Failed to update profile', err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  // Показываем загрузку во время инициализации
  if (initializing) {
    return (
      <Background $night={isNightOps}>
        {isModernWarfare && <ModernBackground />}
        <Container $night={isNightOps}>
          <LoadingSpinner $night={isNightOps} />
        </Container>
        <NotificationContainer notifications={notifications} onRemove={removeNotification} />
      </Background>
    );
  }

  if (loading) {
    return (
      <Background $night={isNightOps}>
        {isModernWarfare && <ModernBackground />}
        <Container $night={isNightOps}>
          <LoadingSpinner $night={isNightOps} />
        </Container>
        <NotificationContainer notifications={notifications} onRemove={removeNotification} />
      </Background>
    );
  }

  if (error || !profile) {
    return (
      <Background $night={isNightOps}>
        {isModernWarfare && <ModernBackground />}
        <Container $night={isNightOps}>
          <ErrorMessage $night={isNightOps}>
            {error || 'Profile not found'}
          </ErrorMessage>
        </Container>
        <NotificationContainer notifications={notifications} onRemove={removeNotification} />
      </Background>
    );
  }

  return (
    <Background $night={isNightOps}>
      {isModernWarfare && <ModernBackground />}
      <Container $night={isNightOps}>
        <Header $night={isNightOps}>
          <TitleSection>
            <Title $night={isNightOps}>
              {profile.firstName} {profile.lastName}
            </Title>
            <Subtitle $night={isNightOps}>
              {profile.isMainReferee ? 'Main Referee' : 'Referee'} • {profile.gameTypes.map(gt => gt.toUpperCase()).join(', ')}
            </Subtitle>
          </TitleSection>
          {isOwnProfile && isRefereeAuthenticated && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {profile.isMainReferee && (
                <EditButton $night={isNightOps} onClick={() => {
                  console.log('Manage Field clicked, navigating to /referee/manage-field');
                  navigate('/referee/manage-field');
                }}>
                  Manage Field
                </EditButton>
              )}
              <EditButton $night={isNightOps} onClick={handleEditProfile}>
                Edit Profile
              </EditButton>
            </div>
          )}
        </Header>

        <InfoGrid>
          <MainContent>
            {/* Statistics Section */}
            <Section $night={isNightOps}>
              <SectionTitle $night={isNightOps}>Game Statistics</SectionTitle>
              <StatsGrid>
                <StatCard $night={isNightOps}>
                  <StatValue $night={isNightOps}>{profile.totalGames}</StatValue>
                  <StatLabel $night={isNightOps}>Total Games</StatLabel>
                </StatCard>
                <StatCard $night={isNightOps}>
                  <StatValue $night={isNightOps}>{profile.totalAirsoftGames}</StatValue>
                  <StatLabel $night={isNightOps}>Airsoft Games</StatLabel>
                </StatCard>
                <StatCard $night={isNightOps}>
                  <StatValue $night={isNightOps}>{profile.totalPaintballGames}</StatValue>
                  <StatLabel $night={isNightOps}>Paintball Games</StatLabel>
                </StatCard>
                <StatCard $night={isNightOps}>
                  <StatValue $night={isNightOps}>{profile.totalSpeedballGames}</StatValue>
                  <StatLabel $night={isNightOps}>Speedball Games</StatLabel>
                </StatCard>
                <StatCard $night={isNightOps}>
                  <StatValue $night={isNightOps}>{profile.rating.toFixed(1)}</StatValue>
                  <StatLabel $night={isNightOps}>Rating</StatLabel>
                </StatCard>
                <StatCard $night={isNightOps}>
                  <StatValue $night={isNightOps}>0</StatValue>
                  <StatLabel $night={isNightOps}>Reviews</StatLabel>
                </StatCard>
              </StatsGrid>
            </Section>

            {/* Assigned Field Section */}
            <Section $night={isNightOps}>
              <SectionTitle $night={isNightOps}>Assigned Fields</SectionTitle>
              {profile.assignedFields.length > 0 ? (
                <div>
                  {profile.assignedFields.map((field: any) => (
                    <FieldCard key={field.id} $night={isNightOps}>
                      <FieldName
                        $night={isNightOps}
                        onClick={() => navigate(`/fields/${field.id}`)}
                      >
                        {field.name}
                      </FieldName>
                      <InfoValue $night={isNightOps} style={{ fontSize: '0.9rem', marginTop: '5px' }}>
                        {field.address}
                      </InfoValue>
                      <InfoValue $night={isNightOps} style={{ fontSize: '0.8rem', marginTop: '5px' }}>
                        Status: {profile.fieldAssignments[field.id] || 'Unknown'}
                      </InfoValue>
                    </FieldCard>
                  ))}
                </div>
              ) : (
                <EmptyState $night={isNightOps}>
                  Not assigned to any fields yet
                </EmptyState>
              )}
            </Section>

            {/* Conducted Games Section */}
            <Section $night={isNightOps}>
              <SectionTitle $night={isNightOps}>Recent Conducted Games</SectionTitle>
              {targetRefereeId && (
                <GamesSection
                  refereeId={targetRefereeId}
                  refereeName={`${profile.firstName} ${profile.lastName}`}
                  supportedGameTypes={profile.gameTypes || ['airsoft', 'paintball', 'speedball']}
                  showFilters={true}
                  showPagination={true}
                />
              )}
            </Section>

            {/* Reviews Section */}
            <Section $night={isNightOps}>
              <SectionTitle $night={isNightOps}>Referee Reviews</SectionTitle>
              <ReviewSummary
                summary={reviewSummary || {
                  rating: profile.rating || 0,
                  ratingConfirmed: profile.ratingConfirmed || 0,
                  reviewCount: 0,
                  reviewCountConfirmed: 0,
                  totalReviews: 0,
                  ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
                  ratingDistributionConfirmed: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
                }}
                loading={reviewsLoading}
                targetType="referee"
                targetId={targetRefereeId}
                showWriteReview={!isOwnProfile}
                canWriteReview={!!user && !myReview}
                onWriteReview={() => setIsReviewModalOpen(true)}
                writeReviewText={myReview ? 'Edit Review' : 'Write Review'}
                writeReviewMessage={field ? 'Fields cannot write reviews' : referee ? 'Referees cannot write reviews' : !user ? 'Log in to write a review' : undefined}
              />
            </Section>

            {/* Achievements Section */}
            <Section $night={isNightOps}>
              <SectionTitle $night={isNightOps}>Referee Achievements</SectionTitle>
              {targetRefereeId && (
                <RefereeAchievements
                  refereeId={targetRefereeId}
                  gameType="all"
                />
              )}
            </Section>
          </MainContent>

          <Sidebar>
            {/* Rank Section */}
            <Section $night={isNightOps}>
              <SectionTitle $night={isNightOps}>Current Rank</SectionTitle>
              <RankSection>
                <RankIcon $night={isNightOps}>
                  {profile.ranks.iconUrl ? (
                    <RankIconImage src={profile.ranks.iconUrl} alt={profile.ranks.rankName} />
                  ) : (
                    <RankIconPlaceholder $night={isNightOps}>No Icon</RankIconPlaceholder>
                  )}
                </RankIcon>
                <RankInfo>
                  <RankName $night={isNightOps}>{profile.ranks.rankName}</RankName>
                  <RankLevel $night={isNightOps}>Level {profile.ranks.level}</RankLevel>
                </RankInfo>
              </RankSection>

              <div>
                <InfoValue $night={isNightOps} style={{ marginBottom: '10px' }}>
                  Points: {profile.ranks.points} / {profile.ranks.nextLevelPoints || 'MAX'}
                </InfoValue>
                <ProgressContainer>
                  <ProgressBar width={profile.ranks.progressToNext} />
                </ProgressContainer>
                <ProgressText $night={isNightOps}>
                  {profile.ranks.progressToNext < 100
                    ? `${profile.ranks.progressToNext.toFixed(1)}% to next level`
                    : 'Max level reached'
                  }
                </ProgressText>
              </div>
            </Section>

            {/* Contact Info Section - show always for own profile, only if has data for others */}
            {(isOwnProfile || hasAnyContactInfo(profile.contactInfo)) && (
              <Section $night={isNightOps}>
                <SectionTitle $night={isNightOps}>Contact Information</SectionTitle>
                {hasAnyContactInfo(profile.contactInfo) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {profile.contactInfo?.workEmail && (
                      <SocialLink $night={isNightOps} href={`mailto:${profile.contactInfo.workEmail}`}>
                        <SocialIcon>📧</SocialIcon>
                        {profile.contactInfo.workEmail}
                      </SocialLink>
                    )}
                    {profile.contactInfo?.phone && (
                      <SocialLink $night={isNightOps} href={`tel:${profile.contactInfo.phone}`}>
                        <SocialIcon>📞</SocialIcon>
                        {profile.contactInfo.phone}
                      </SocialLink>
                    )}
                    {profile.contactInfo?.facebook && (
                      <SocialLink $night={isNightOps} href={profile.contactInfo.facebook} target="_blank" rel="noopener noreferrer">
                        <SocialIcon>👤</SocialIcon>
                        Facebook
                      </SocialLink>
                    )}
                    {profile.contactInfo?.instagram && (
                      <SocialLink $night={isNightOps} href={profile.contactInfo.instagram} target="_blank" rel="noopener noreferrer">
                        <SocialIcon>📷</SocialIcon>
                        Instagram
                      </SocialLink>
                    )}
                    {profile.contactInfo?.twitter && (
                      <SocialLink $night={isNightOps} href={profile.contactInfo.twitter} target="_blank" rel="noopener noreferrer">
                        <SocialIcon>🐦</SocialIcon>
                        Twitter
                      </SocialLink>
                    )}
                    {profile.contactInfo?.telegram && (
                      <SocialLink $night={isNightOps} href={profile.contactInfo.telegram} target="_blank" rel="noopener noreferrer">
                        <SocialIcon>✈️</SocialIcon>
                        Telegram
                      </SocialLink>
                    )}
                    {profile.contactInfo?.website && (
                      <SocialLink $night={isNightOps} href={profile.contactInfo.website} target="_blank" rel="noopener noreferrer">
                        <SocialIcon>🌐</SocialIcon>
                        Website
                      </SocialLink>
                    )}
                  </div>
                ) : (
                  <EmptyState $night={isNightOps}>
                    No contact information added yet. Click "Edit Profile" to add your contact details.
                  </EmptyState>
                )}
              </Section>
            )}

            {/* My Schedule Section - only for own profile */}
            {isOwnProfile && isRefereeAuthenticated && targetRefereeId && (
              <RefereeScheduleSection refereeId={targetRefereeId} />
            )}
          </Sidebar>
        </InfoGrid>
      </Container>
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />

      {/* Edit Profile Modal */}
      {isEditModalOpen && (
        <ModalOverlay onClick={() => setIsEditModalOpen(false)}>
          <ModalContent $night={isNightOps} onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle $night={isNightOps}>Edit Profile</ModalTitle>
              <CloseButton $night={isNightOps} onClick={() => setIsEditModalOpen(false)}>
                &times;
              </CloseButton>
            </ModalHeader>

            {/* Personal Information */}
            <SectionTitle $night={isNightOps} style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
              Personal Information
            </SectionTitle>

            <FormGrid>
              <FormGroup>
                <FormLabel $night={isNightOps}>First Name</FormLabel>
                <FormInput
                  $night={isNightOps}
                  type="text"
                  placeholder="John"
                  value={editingProfile.firstName}
                  onChange={(e) => setEditingProfile({ ...editingProfile, firstName: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel $night={isNightOps}>Last Name</FormLabel>
                <FormInput
                  $night={isNightOps}
                  type="text"
                  placeholder="Doe"
                  value={editingProfile.lastName}
                  onChange={(e) => setEditingProfile({ ...editingProfile, lastName: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel $night={isNightOps}>Phone</FormLabel>
                <PhoneInput
                  $night={isNightOps}
                  value={editingProfile.phone}
                  onChange={(value) => setEditingProfile({ ...editingProfile, phone: value })}
                  onValidationChange={(isValid) => setPhoneValid(isValid)}
                />
              </FormGroup>
            </FormGrid>

            {/* Contact Information */}
            <SectionTitle $night={isNightOps} style={{ fontSize: '1.1rem', marginBottom: '15px', marginTop: '25px' }}>
              Contact Information (Public)
            </SectionTitle>

            <FormGrid>
              <FormGroup>
                <FormLabel $night={isNightOps}>Work Email</FormLabel>
                <FormInput
                  $night={isNightOps}
                  type="email"
                  placeholder="work@example.com"
                  value={editingProfile.contactInfo.workEmail || ''}
                  onChange={(e) => setEditingProfile({
                    ...editingProfile,
                    contactInfo: { ...editingProfile.contactInfo, workEmail: e.target.value }
                  })}
                  onBlur={(e) => validateField('workEmail', e.target.value)}
                />
                {validationErrors.workEmail && <FormError>{validationErrors.workEmail}</FormError>}
              </FormGroup>

              <FormGroup>
                <FormLabel $night={isNightOps}>Public Phone</FormLabel>
                <PhoneInput
                  $night={isNightOps}
                  value={editingProfile.contactInfo.phone || ''}
                  onChange={(value) => setEditingProfile({
                    ...editingProfile,
                    contactInfo: { ...editingProfile.contactInfo, phone: value }
                  })}
                  onValidationChange={(isValid) => setPublicPhoneValid(isValid)}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel $night={isNightOps}>Facebook</FormLabel>
                <FormInput
                  $night={isNightOps}
                  type="url"
                  placeholder="https://facebook.com/username"
                  value={editingProfile.contactInfo.facebook || ''}
                  onChange={(e) => setEditingProfile({
                    ...editingProfile,
                    contactInfo: { ...editingProfile.contactInfo, facebook: e.target.value }
                  })}
                  onBlur={(e) => validateField('facebook', e.target.value)}
                />
                {validationErrors.facebook && <FormError>{validationErrors.facebook}</FormError>}
              </FormGroup>

              <FormGroup>
                <FormLabel $night={isNightOps}>Instagram</FormLabel>
                <FormInput
                  $night={isNightOps}
                  type="url"
                  placeholder="https://instagram.com/username"
                  value={editingProfile.contactInfo.instagram || ''}
                  onChange={(e) => setEditingProfile({
                    ...editingProfile,
                    contactInfo: { ...editingProfile.contactInfo, instagram: e.target.value }
                  })}
                  onBlur={(e) => validateField('instagram', e.target.value)}
                />
                {validationErrors.instagram && <FormError>{validationErrors.instagram}</FormError>}
              </FormGroup>

              <FormGroup>
                <FormLabel $night={isNightOps}>Twitter</FormLabel>
                <FormInput
                  $night={isNightOps}
                  type="url"
                  placeholder="https://twitter.com/username"
                  value={editingProfile.contactInfo.twitter || ''}
                  onChange={(e) => setEditingProfile({
                    ...editingProfile,
                    contactInfo: { ...editingProfile.contactInfo, twitter: e.target.value }
                  })}
                  onBlur={(e) => validateField('twitter', e.target.value)}
                />
                {validationErrors.twitter && <FormError>{validationErrors.twitter}</FormError>}
              </FormGroup>

              <FormGroup>
                <FormLabel $night={isNightOps}>Telegram</FormLabel>
                <FormInput
                  $night={isNightOps}
                  type="url"
                  placeholder="https://t.me/username"
                  value={editingProfile.contactInfo.telegram || ''}
                  onChange={(e) => setEditingProfile({
                    ...editingProfile,
                    contactInfo: { ...editingProfile.contactInfo, telegram: e.target.value }
                  })}
                  onBlur={(e) => validateField('telegram', e.target.value)}
                />
                {validationErrors.telegram && <FormError>{validationErrors.telegram}</FormError>}
              </FormGroup>

              <FormGroup>
                <FormLabel $night={isNightOps}>Website</FormLabel>
                <FormInput
                  $night={isNightOps}
                  type="url"
                  placeholder="https://yourwebsite.com"
                  value={editingProfile.contactInfo.website || ''}
                  onChange={(e) => setEditingProfile({
                    ...editingProfile,
                    contactInfo: { ...editingProfile.contactInfo, website: e.target.value }
                  })}
                  onBlur={(e) => validateField('website', e.target.value)}
                />
                {validationErrors.website && <FormError>{validationErrors.website}</FormError>}
              </FormGroup>
            </FormGrid>

            <ModalButtons>
              <CancelButton $night={isNightOps} onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </CancelButton>
              <SaveButton
                $night={isNightOps}
                disabled={savingProfile}
                onClick={handleSaveProfile}
              >
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </SaveButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Review Modal */}
      {targetRefereeId && (
        <ReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          targetType="referee"
          targetId={targetRefereeId}
          targetName={`${profile?.firstName} ${profile?.lastName}`}
          existingReview={myReview}
          onReviewSubmitted={reloadReviews}
        />
      )}
    </Background>
  );
};

export default RefereeProfilePage;
